import { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useAppDispatch } from "../../redux/hooks";
import Grid from "@mui/material/Grid2";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import editIcon from "../../assets/edit-pencil-icon.png";
import colors from "../../theme/Colours";
import {
  commonButtonStyles,
  commonPrimaryButtonStyles,
  displayFlexJustifyEnd,
  editIconStyles,
  customerPageTitle,
  titleContainer,
  customerContainer,
  dropdownStyles,
  generateAgreementButton,
  downloadAgreementButton,
} from "./CustomerDetailsStyles";
import { FormProvider, useForm } from "react-hook-form";
import { step1Fields, step2Fields, step3Fields } from "./steps";
import {
  customerDetailsStep1,
  customerDetailsStep2,
  customerDetailsStep3,
} from "../../schemas/customerDetails";
import { yupResolver } from "@hookform/resolvers/yup";
import { CustomerStep } from "./CustomerStep";
import {
  checkBox,
  checkBoxChecked,
  titleStyle,
  fieldTitleStyle,
} from "./customerStepStyles";
import { Switch } from "../../common/Switch";
import {
  customerDetailsStep1Defaults,
  customerDetailsStep2Defaults,
  customerDetailsStep3Defaults,
} from "../../constants/formDefaultValues/customerDetailsDefaultValues";
import { createPayload } from "./createPayload";
import getPopulatedValues from "./populateDefaultValues";
import checkIcon from "../../assets/checkBox_check.svg";
import Toast from "../../common/Toast";
import { setLoading } from "../../redux/features/slices/appSlice";
import {
  deleteCustomer,
  createNewCustomer,
  editCustomer,
  getPresignedUrls,
  getCustomerById,
} from "../../api/customerAPI";
import bucketMapping from "../../constants/S3BuketMapping";
import useIsMobile from "../../common/mobileVersion/useIsMobile";
import {
  downloadFileFromS3,
  uploadSingleFileWithPresignedUrl,
} from "../../utils/files";
import CustomDropdown from "../../common/CustomDropdown";
import { fetchSalesReps, SalesRep } from "../../api/customerAPI";
import { stateGstCodeMap } from "../../constants/stateList";

const CheckIcon = () => (
  <img src={checkIcon} alt="check-icon" height="8px" width="11px" />
);

const getSelectedSalesRepObject = (
  selectedId: string,
  salesReps: SalesRep[]
): { id: string; role: string } | null => {
  if (!selectedId || selectedId === "NA") return null;

  const rep = salesReps.find((r) => r.id === selectedId);
  return rep ? { id: rep.id, role: rep.role } : null;
};

const CustomerDetails = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isCreateNewCustomer = location.pathname.includes("create-new-customer");
  const userID = isCreateNewCustomer
    ? undefined
    : location.pathname.split("/").pop();

  const [userData, setUserData] = useState<
    | {
        step1: any;
        step2: any;
        step3: any;
        customerAgreements: any;
      }
    | undefined
  >();

  const dispatch = useAppDispatch();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [error, setError] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const [loadingData, setLoadingData] = useState(!isCreateNewCustomer);
  const [isEditable, setIsEditable] = useState(isCreateNewCustomer);

  const [step1DefaultValues, setStep1DefaultValues] = useState(
    customerDetailsStep1Defaults
  );
  const [step2DefaultValues, setStep2DefaultValues] = useState(
    customerDetailsStep2Defaults
  );
  const [step3DefaultValues, setStep3DefaultValues] = useState(
    customerDetailsStep3Defaults
  );

  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [selectedSalesRep, setSelectedSalesRep] = useState<string>("");
  const initialSalesRepRef = useRef<string>("");

  const [defaultAdvancedPayment, setDefaultAdvancedPayment] = useState(false);

  const orgId = localStorage.getItem("orgId");

  const fetchSalesRepsData = async () => {
    try {
      const data = await fetchSalesReps();
      setSalesReps(data);
    } catch (error) {
      console.error("Error fetching sales reps:", error);
      setError(true);
      setSalesReps([]);
    }
  };

  useEffect(() => {
    fetchSalesRepsData();
  }, []);

  useEffect(() => {
    if (!isCreateNewCustomer) {
      const fetchUserData = async () => {
        try {
          const response = await getCustomerById(userID);
          if (!response) {
            console.error("Failed to fetch customer data");
          } else {
            console.log("Fetched customer data:", response);
          }
          console.log(response, "resp");
          const values = getPopulatedValues(response.data.data);
          setUserData(values);

          setDefaultAdvancedPayment(values.advancePaymentMandatory);
          setAdvanceMandatory(values.advancePaymentMandatory);
          setDefaultEmailActivation(values.emailActivation);
          setEmailActivation(values.emailActivation);

          const assignedRep = response.data.data.assignedSalesRep;
          const salesRepId = assignedRep?.id || "NA";
          setSelectedSalesRep(salesRepId);
          initialSalesRepRef.current = salesRepId;

          setStep1DefaultValues(values.step1);
          setStep2DefaultValues(values.step2);
          setStep3DefaultValues(values.step3);

          step1Control.reset(values.step1);
          step2Control.reset(values.step2);
          step3Control.reset(values.step3);
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoadingData(false);
        }
      };
      fetchUserData();
    }
  }, [userID, isCreateNewCustomer]);

  // Refresh customer data when returning from agreement page
  useEffect(() => {
    if (!isCreateNewCustomer && location.state?.refreshData) {
      const fetchUserData = async () => {
        try {
          const response = await getCustomerById(userID);
          if (!response) {
            console.error("Failed to fetch customer data");
          } else {
            console.log("Refreshed customer data:", response);
          }
          const values = getPopulatedValues(response.data.data);
          setUserData(values);

          setStep1DefaultValues(values.step1);
          setStep2DefaultValues(values.step2);
          setStep3DefaultValues(values.step3);

          step1Control.reset(values.step1);
          step2Control.reset(values.step2);
          step3Control.reset(values.step3);
        } catch (error) {
          console.error("Error refreshing user data:", error);
        }
      };
      fetchUserData();
      // Clear the refresh state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, userID, isCreateNewCustomer, navigate]);

  const uploadMultipleFilesWithPresignedUrls = async (
    presignedUrls: { presignedUrl: string; key: string; fileName: string }[],
    documents: ({
      fileContent: File | Blob;
      fileType: string;
      documentType: string;
      fileName: string;
    } | null)[]
  ) => {
    // Filter out null documents before processing
    const validDocuments = documents.filter(
      (
        doc
      ): doc is {
        fileContent: File | Blob;
        fileType: string;
        documentType: string;
        fileName: string;
      } => doc !== null && doc.fileName.trim() !== ""
    );

    dispatch(setLoading(true));
    const uploadedDocuments = await Promise.all(
      presignedUrls.map(async (presignedFile, index) => {
        const document = validDocuments[index]; // Ensure correct mapping

        if (!document) {
          console.warn(
            `Skipping upload: No document found for ${presignedFile.fileName}`
          );
          return null;
        }

        const isUploaded = await uploadSingleFileWithPresignedUrl(
          presignedFile.presignedUrl,
          document.fileContent,
          document.fileType
        );

        if (isUploaded) {
          return {
            documentId: presignedFile.presignedUrl, // S3 URL
            type: document.documentType,
            fileName: document.fileName,
            key: presignedFile.key, // S3 key
          };
        } else {
          throw new Error(`Upload failed for ${document.fileName}`);
        }
      })
    );
    dispatch(setLoading(false));

    // Remove null values before returning
    return uploadedDocuments.filter((doc) => doc !== null);
  };

  const step1Control = useForm({
    resolver: yupResolver(customerDetailsStep1),
    defaultValues: step1DefaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (isCreateNewCustomer) {
      step1Control.setValue("comp_vat_cert", "");
      step2Control.setValue("lic_gst_cert", "");
      step3Control.setValue("bank_cancelled_cheque", "");
      if (typeof step2Control.getValues("licenseType") === "undefined") {
        step2Control.setValue(
          "licenseType",
          step2DefaultValues?.licenseType ?? ""
        );
      }
    }
  }, []);

  const step2Control = useForm({
    resolver: yupResolver(customerDetailsStep2),
    defaultValues: step2DefaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const watchedState = step1Control.watch("state");
  useEffect(() => {
    if (watchedState) {
      const code = stateGstCodeMap[watchedState];
      if (code) step2Control.setValue("gstStateCode", code);
    }
  }, [watchedState]);

  const step3Control = useForm({
    resolver: yupResolver(customerDetailsStep3),
    defaultValues: step3DefaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { control, setValue, trigger, clearErrors } = useForm({
    defaultValues: {
      creditLimit: "",
    },
    mode: "onBlur",
  });

  const collectValidationErrors = () => {
    const errors: string[] = [];

    // Helper function to get error message for a field
    const getFieldError = (
      formControl: any,
      fieldName: string,
      fieldLabel: string
    ) => {
      try {
        const fieldState = formControl.getFieldState(fieldName);
        return fieldState?.error?.message
          ? `${fieldLabel}: ${fieldState.error.message}`
          : null;
      } catch (error) {
        return null;
      }
    };

    // Common fields to check in each step
    const step1Fields = [
      "outletName",
      "companyGST",
      "companyPAN",
      "registeredAddress",
      "vatNumber",
      "state",
      "pincode",
      "phoneNumber",
      "emailID",
      "gstStateCode",
      "authorizedSignatory",
    ];
    const step2Fields = [
      "licenseType",
      "licenseName",
      "licenseGST",
      "licensePAN",
      "licenseAddress",
      "customerExciseRegistrationNumber",
    ];
    const step3Fields = [
      "bankName",
      "bankBranch",
      "accountType",
      "accountNumber",
      "ifscCode",
      "beneficiaryName",
      "accountsDepartmentPOCName",
      "accountsDepartmentPOCNumber",
    ];

    // File upload fields to check (with human-readable labels)
    const fileFieldLabels: Record<string, string> = {
      comp_gst_cert: "Company GST Certificate",
      comp_pan_card: "Company PAN Card",
      comp_vat_cert: "Company VAT Certificate",
      lic_rvb_license: "RVB License Copy",
      lic_license_pan: "License PAN Card",
      lic_gst_cert: "License GST Certificate",
      bank_cancelled_cheque: "Cancelled Cheque",
    };
    const step1FileFields = ["comp_gst_cert", "comp_pan_card", "comp_vat_cert"];
    const step2FileFields = [
      "lic_rvb_license",
      "lic_license_pan",
      "lic_gst_cert",
    ];
    const step3FileFields = ["bank_cancelled_cheque"];

    // Collect errors from step1 fields
    step1Fields.forEach((fieldName) => {
      const error = getFieldError(step1Control, fieldName, fieldName);
      if (error) errors.push(error);
    });

    // Collect errors from step2 fields
    step2Fields.forEach((fieldName) => {
      const error = getFieldError(step2Control, fieldName, fieldName);
      if (error) errors.push(error);
    });

    // Collect errors from step3 fields
    step3Fields.forEach((fieldName) => {
      const error = getFieldError(step3Control, fieldName, fieldName);
      if (error) errors.push(error);
    });

    // Collect errors from step1 file fields
    step1FileFields.forEach((fieldName) => {
      const error = getFieldError(
        step1Control,
        fieldName,
        fileFieldLabels[fieldName] || fieldName
      );
      if (error) errors.push(error);
    });

    // Collect errors from step2 file fields
    step2FileFields.forEach((fieldName) => {
      const error = getFieldError(
        step2Control,
        fieldName,
        fileFieldLabels[fieldName] || fieldName
      );
      if (error) errors.push(error);
    });

    // Collect errors from step3 file fields
    step3FileFields.forEach((fieldName) => {
      const error = getFieldError(
        step3Control,
        fieldName,
        fileFieldLabels[fieldName] || fieldName
      );
      if (error) errors.push(error);
    });

    // Check credit limit error
    const creditLimitError = getFieldError(
      control,
      "creditLimit",
      "Credit Limit"
    );
    if (creditLimitError) errors.push(creditLimitError);

    return errors;
  };

  const validateAllSteps = async () => {
    const step1IsValid = await step1Control.trigger();
    const step2IsValid = await step2Control.trigger();
    const step3IsValid = await step3Control.trigger();
    const creditLimitIsValid = await trigger("creditLimit");

    // Collect validation errors
    const errors = collectValidationErrors();
    setValidationErrors(errors);

    return step1IsValid && step2IsValid && step3IsValid && creditLimitIsValid;
  };

  const [advanceMandatory, setAdvanceMandatory] = useState(false);

  const [showEditForm, setShowEditForm] = useState(false);

  const [emailActivation, setEmailActivation] = useState(true);
  const [defaultEmailActivation, setDefaultEmailActivation] = useState(true);

  const handleReset = () => {
    step1Control.reset(step1DefaultValues);
    step2Control.reset(step2DefaultValues);
    step3Control.reset(step3DefaultValues);
    setAdvanceMandatory(defaultAdvancedPayment);
    setEmailActivation(defaultEmailActivation);
    setSelectedSalesRep(initialSalesRepRef.current);
    setValidationErrors([]); // Clear validation errors on reset
  };

  type ValidDocument = {
    fileName: string;
    fileContent: string;
    documentType: string;
    fileType: string;
  };

  const extractValidDocuments = (
    documents: (ValidDocument | null)[]
  ): Omit<ValidDocument, "fileContent">[] => {
    return documents
      ?.filter(
        (doc): doc is ValidDocument =>
          !!doc &&
          typeof doc.fileName === "string" &&
          doc.fileName.trim() !== "" &&
          typeof doc.fileType === "string" &&
          typeof doc.documentType === "string"
      )
      .map(({ fileName, fileType, documentType }) => ({
        fileName,
        fileType,
        documentType,
      }));
  };

  const handleSubmit = async () => {
    setError(false);
    if (isCreateNewCustomer) {
      const isValid = await validateAllSteps();

      const assignedSalesRepObj = getSelectedSalesRepObject(
        selectedSalesRep,
        salesReps
      );

      const formData = {
        company: step1Control.getValues(),
        license: step2Control.getValues(),
        bank: step3Control.getValues(),
        advancePaymentMandatory: advanceMandatory,
        emailActivation: isCreateNewCustomer ? true : emailActivation,
        assignedSalesRep: assignedSalesRepObj,
      };

      const payload = await createPayload(formData);

      if (isValid) {
        try {
          const updatedPayload = extractValidDocuments(payload.documents);
          const presignedurls =
            (await getPresignedUrls(updatedPayload, bucketMapping.customer)) ||
            [];
          const finalDocumentData: any[] =
            await uploadMultipleFilesWithPresignedUrls(
              presignedurls,
              payload.documents
            );
          await createNewCustomer(payload, finalDocumentData);
          setToastMessage("Customer created successfully");
          setTimeout(() => {
            navigate(`/maltora-seller/all-customers`);
          }, 2000);
        } catch (error: any) {
          setError(true);
          const backendMessage = error?.response?.data?.message;
          setToastMessage(backendMessage || "Something went wrong");
          console.error("Error creating Customer:", error);
        }
        setShowToast(true);
        setShowEditForm(false);
      }
    } else {
      if (isEditable) {
        const isValid = await validateAllSteps();

        const assignedSalesRepObj = getSelectedSalesRepObject(
          selectedSalesRep,
          salesReps
        );

        const formData = {
          company: step1Control.getValues(),
          license: step2Control.getValues(),
          bank: step3Control.getValues(),
          advancePaymentMandatory: advanceMandatory,
          emailActivation: emailActivation,
          assignedSalesRep: assignedSalesRepObj,
        };

        const oldDocuments = [
          step1DefaultValues.comp_gst_cert,
          step1DefaultValues.comp_pan_card,
          step1DefaultValues.comp_vat_cert,
          step2DefaultValues.lic_rvb_license,
          step2DefaultValues.lic_license_pan,
          step2DefaultValues.lic_gst_cert,
          step3DefaultValues.bank_cancelled_cheque,
        ];

        const payload = await createPayload(formData);

        const deletedDocuments = oldDocuments.filter((doc: any) => {
          return payload.documents.find(
            (newDoc: any) =>
              newDoc.documentType === doc?.originalObj?.type &&
              newDoc.fileName === ""
          );
        });

        const documents = {
          oldDocuments: oldDocuments,
          newDocuments: payload.documents.filter((doc: any) => doc.fileContent),
          deletedDocuments: deletedDocuments.map((doc: any) => doc.originalObj),
        };

        // **2. Upload New Documents to S3**
        let uploadedDocuments: any[] = [];
        if (documents.newDocuments.length > 0) {
          const updatedPayload = extractValidDocuments(payload.documents);
          const presignedurls =
            (await getPresignedUrls(updatedPayload, bucketMapping.customer)) ||
            [];

          uploadedDocuments = await uploadMultipleFilesWithPresignedUrls(
            presignedurls,
            documents.newDocuments
          );
        }

        // **3. Update Old Documents or Add New Ones**
        const finalNewDocumentData: any[] = [];
        for (const document of documents.newDocuments) {
          const uploadedFile = uploadedDocuments.find(
            (item) => item.fileName === document?.fileName
          );
          const url = uploadedFile?.documentId;
          const Key = uploadedFile?.key;

          if (!Key || !url) {
            throw new Error(
              `Missing S3 upload data for file: ${document?.fileName}`
            );
          }
          const documentsData = {
            documentId: url,
            type: document?.documentType,
            fileName: document?.fileName,
            key: Key,
          };
          finalNewDocumentData.push(documentsData);
        }

        documents.newDocuments = finalNewDocumentData;

        const updatePayload = {
          ...payload,
          documents: documents,
        };

        if (isValid) {
          try {
            if (userID) {
              await editCustomer(userID, updatePayload);
            }
            setToastMessage("Customer updated successfully");
            setTimeout(() => {
              navigate(`/maltora-seller/all-customers`);
            }, 2000);
          } catch (error) {
            setToastMessage("Failed to update customer details");
            setError(true);
            console.error("Error updating Customer:", error);
          }
          setShowToast(true);
          setShowEditForm(false);
        }
      } else {
        try {
          let response;
          if (userID) {
            response = await deleteCustomer(userID, userData?.step1.emailID);
          }

          if (response?.status === 200) {
            setToastMessage("Customer deleted successfully");
            setTimeout(() => {
              navigate(`/maltora-seller/all-customers`);
            }, 2000);
          }
        } catch (error) {
          setToastMessage("Failed to delete customer");
          setError(true);
          console.error("Error deleting Customer:", error);
        }
        setShowToast(true);
      }
    }
  };

  const salesRepOptions = [
    { value: "NA", label: "NA" },
    ...salesReps.map((rep) => ({
      value: rep.id,
      label: rep.name,
    })),
  ];

  const handleEditClick = () => {
    setIsEditable(true);
    setShowEditForm(true);
  };

  const handleAdvanceMandatoryChange = () => {
    if (isCreateNewCustomer || isEditable) {
      setAdvanceMandatory(!advanceMandatory);
      if (!advanceMandatory) {
        setValue("creditLimit", "");
        clearErrors("creditLimit");
      }
    }
  };

  const creditLimitValidation = {
    required: !advanceMandatory ? "Credit limit is required" : false,
    pattern: {
      value: /^\d+(\.\d{1,2})?$/,
      message: "Invalid input: Please enter a numeric value",
    },
  };

  const hasCustomerAgreementGenerated = useMemo(() => {
    // Always disable during loading or when creating new customer
    if (loadingData || isCreateNewCustomer || !userData) {
      console.log("=== DOWNLOAD BUTTON DEBUG - EARLY RETURN ===", {
        customerId: params.id,
        isCreateNewCustomer,
        loadingData,
        userDataExists: !!userData,
        reason: loadingData
          ? "Still loading"
          : isCreateNewCustomer
            ? "Creating new customer"
            : "No user data",
        finalResult: false,
      });
      console.log("===========================");
      return false;
    }

    const unsignedAgreement = userData?.customerAgreements?.unsigned;
    const hasDocumentId =
      unsignedAgreement &&
      typeof unsignedAgreement.documentId === "string" &&
      unsignedAgreement.documentId.trim() !== "";

    console.log("=== DOWNLOAD BUTTON DEBUG ===", {
      customerId: params.id,
      isCreateNewCustomer,
      loadingData,
      userDataExists: !!userData,
      customerAgreements: userData?.customerAgreements,
      unsignedAgreement,
      documentId: unsignedAgreement?.documentId,
      documentIdType: typeof unsignedAgreement?.documentId,
      hasDocumentId,
      finalResult: hasDocumentId,
    });
    console.log("===========================");

    return hasDocumentId;
  }, [userData, params.id, isCreateNewCustomer, loadingData]);

  const hasCustomerAgreementSigned = useMemo(() => {
    return Boolean(userData?.customerAgreements?.signed?.documentId);
  }, [userData]);

  const handleGenerateCustomerAgreement = async () => {
    const customerId = params.id || "";
    navigate(`/maltora-seller/customer/${customerId}/agreement`);
  };

  const handleDownloadSignedAgreement = async () => {
    if (hasCustomerAgreementGenerated) {
      try {
        const customerId = params.id || "";
        // Use unsigned agreement documentId for download
        const documentId = userData?.customerAgreements?.unsigned?.documentId;

        if (!documentId) {
          throw new Error("Agreement document ID is missing");
        }

        // Download the file using the utility function
        await downloadFileFromS3(
          documentId,
          `customer-agreement-${customerId}-unsigned.pdf`
        );
      } catch (error) {
        console.error("Failed to download customer agreement:", error);
        setError(true);
        setShowToast(true);
        setToastMessage("Failed to download customer agreement");
      }
    }
  };

  return (
    <>
      <Box sx={titleContainer}>
        <Typography sx={customerPageTitle}>
          {isCreateNewCustomer ? "Add New Customer" : "Edit Customer Details"}
        </Typography>
      </Box>

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />

      <Box>
        <Card sx={customerContainer}>
          <CardContent sx={{ padding: 0 }}>
            <Grid
              size={6}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mb: 2,
              }}
            >
              {!showEditForm && !isCreateNewCustomer && (
                <Button
                  onClick={handleEditClick}
                  variant="outlined"
                  sx={{
                    border: "1px solid #FF6B00",
                    color: "#FF6B00",
                    backgroundColor: "#FFF8F3",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "13px",
                    minWidth: "unset",
                    width: "92px",
                    height: "36px",
                    borderRadius: "10px",
                    padding: "6px 14px",
                    boxShadow: "0px 2px 8px rgba(255, 107, 0, 0.08)",

                    "& .MuiButton-startIcon": {
                      marginRight: "6px",
                    },

                    "&:hover": {
                      border: "1px solid #FF6B00",
                      backgroundColor: "#FFF1E7",
                      boxShadow: "0px 4px 12px rgba(255, 107, 0, 0.12)",
                    },
                  }}
                >
                  Edit
                </Button>
              )}
            </Grid>

            {!loadingData ? (
              <>
                <FormProvider {...step1Control}>
                  <CustomerStep
                    control={step1Control.control}
                    fields={step1Fields}
                    title="STEP 1: Company Information"
                    disabled={!isCreateNewCustomer && !isEditable}
                    isFirstTitle={true}
                    isCreateNewCustomer={isCreateNewCustomer}
                  />
                </FormProvider>

                <FormProvider {...step2Control}>
                  <CustomerStep
                    control={step2Control.control}
                    fields={step2Fields}
                    title="STEP 2: License Information"
                    disabled={!isCreateNewCustomer && !isEditable}
                    isFirstTitle={false}
                    isCreateNewCustomer={isCreateNewCustomer}
                  />
                </FormProvider>

                <FormProvider {...step3Control}>
                  <CustomerStep
                    control={step3Control.control}
                    fields={step3Fields}
                    title="STEP 3: Bank Information"
                    disabled={!isCreateNewCustomer && !isEditable}
                    isFirstTitle={false}
                  />
                </FormProvider>

                {!isCreateNewCustomer && (
                  <Box>
                    <Typography
                      mt={2}
                      mb={2}
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        paddingBottom: "8px",
                        fontSize: isMobile ? "12px" : "inherit",
                        fontFamily: "Montserrat",
                      }}
                    >
                      Customer Agreement
                    </Typography>
                    <Box display="flex" gap={2}>
                      <Button
                        variant="outlined"
                        sx={{ ...generateAgreementButton, width: "270px" }}
                        onClick={handleGenerateCustomerAgreement}
                      >
                        {hasCustomerAgreementGenerated
                          ? "View Agreement"
                          : "Generate Agreement"}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ArrowDownwardIcon sx={{ fontSize: 28 }} />}
                        sx={{ ...downloadAgreementButton, width: "300px" }}
                        onClick={handleDownloadSignedAgreement}
                        disabled={
                          loadingData ||
                          isCreateNewCustomer ||
                          !hasCustomerAgreementGenerated
                        }
                      >
                        Download Agreement
                      </Button>
                    </Box>
                  </Box>
                )}
                {/* Assign Sales Rep Dropdown */}
                <Box sx={{ mt: "60px" }}>
                  <Typography sx={[titleStyle, { mb: "16px" }]}>
                    {isCreateNewCustomer ? "Assign Sales Rep" : "Assigned To"}
                  </Typography>
                  <Box sx={{ width: isMobile ? "100%" : "50%" }}>
                    <CustomDropdown
                      label="Select"
                      value={
                        isCreateNewCustomer
                          ? selectedSalesRep || ""
                          : selectedSalesRep || "NA"
                      }
                      options={salesRepOptions}
                      onChange={(value) => setSelectedSalesRep(value)}
                      disabled={!isCreateNewCustomer && !isEditable}
                      customStyles={{
                        ...dropdownStyles(!isCreateNewCustomer && !isEditable),
                        menu: { maxHeight: "250px", overflowY: "auto" },
                      }}
                    />
                  </Box>
                </Box>
                {/* Advance Payment */}
                <Box display={"flex"} alignItems={"center"} mt={"38px"}>
                  <FormControlLabel
                    disabled={!isCreateNewCustomer && !isEditable}
                    control={
                      <Checkbox
                        checked={advanceMandatory}
                        onChange={handleAdvanceMandatoryChange}
                        icon={<Box sx={checkBox} />}
                        checkedIcon={
                          <Box sx={checkBoxChecked}>
                            <CheckIcon />
                          </Box>
                        }
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography sx={[titleStyle, { m: 0 }]}>
                          Advance Payment Mandatory
                        </Typography>
                        <Tooltip
                          title="This option ensures that the customer needs to pay some amount before the order gets ready for dispatch."
                          arrow
                        >
                          <InfoIcon
                            sx={{
                              fontSize: 16,
                              color: colors.buttonBGColor,
                              cursor: "pointer",
                              "&:hover": {
                                opacity: 0.8,
                              },
                            }}
                          />
                        </Tooltip>
                      </Box>
                    }
                    sx={{ display: "flex", alignItems: "center" }}
                  />
                </Box>

                <Box component="form" onSubmit={(e: any) => e.preventDefault()}>
                  <Divider sx={{ marginBottom: "36px", marginTop: "60px" }} />

                  <Box>
                    <Grid container spacing={2}>
                      <Grid size={[0, 6]} />

                      <Grid
                        size={[12, 6, 3]}
                        sx={{
                          ...displayFlexJustifyEnd,
                        }}
                      >
                        {!isCreateNewCustomer && isEditable && (
                          <Button
                            variant="outlined"
                            style={commonButtonStyles}
                            onClick={handleReset}
                          >
                            Reset
                          </Button>
                        )}
                      </Grid>
                      <Grid
                        size={[12, 6, 3]}
                        sx={{
                          ...displayFlexJustifyEnd,
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          sx={commonPrimaryButtonStyles}
                          onClick={handleSubmit}
                        >
                          {isCreateNewCustomer
                            ? "Create"
                            : isEditable
                              ? "Save"
                              : "Delete"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </>
            ) : (
              <Typography>Loading...</Typography>
            )}
          </CardContent>
        </Card>

        {/* Validation Errors Display */}
        {validationErrors.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#856404",
                mb: 1,
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              Please fix the following validation errors:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, color: "#856404" }}>
              {validationErrors.map((error, index) => (
                <Typography
                  component="li"
                  key={index}
                  sx={{ mb: 0.5, fontSize: "14px" }}
                >
                  {error}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default CustomerDetails;
